import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { getFaqItems, deleteFaqItem } from '../../lib/api'
import { Button } from '../../components/ui/Button'
import { Badge } from '../../components/ui/Badge'
import { Spinner } from '../../components/ui/Spinner'
import { PageHeader } from '../../components/ui/PageHeader'
import { EmptyState } from '../../components/ui/EmptyState'
import { ConfirmModal, useConfirm } from '../../components/ui/ConfirmModal'

export default function FaqList() {
  const qc = useQueryClient()
  const { confirm, close, state: confirmState } = useConfirm()
  const { data, isLoading } = useQuery({ queryKey: ['faq'], queryFn: getFaqItems })
  const deleteMut = useMutation({
    mutationFn: deleteFaqItem,
    onSuccess: (_, deletedId) => { qc.setQueryData(['faq'], old => (old ?? []).filter(f => f.id !== deletedId)); toast.success('FAQ item deleted') },
    onError: (e) => toast.error(e.message),
  })

  return (
    <div>
      <PageHeader
        title="FAQ"
        description="Frequently asked questions"
        action={<Link to="/faq/new"><Button><i className="ti ti-plus" /> New Question</Button></Link>}
      />

      <ConfirmModal open={confirmState.open} onClose={close} title={confirmState.title} message={confirmState.message} loading={deleteMut.isPending} onConfirm={() => { confirmState.onConfirm(); close() }} />

      {isLoading ? (
        <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
      ) : !data?.length ? (
        <EmptyState
          icon="ti-help-circle"
          title="No FAQ items yet"
          description="Add your first frequently asked question"
          action={<Link to="/faq/new"><Button size="sm">Add FAQ</Button></Link>}
        />
      ) : (
        <div className="rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide w-8">#</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Question</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Order</th>
                <th className="text-left px-5 py-3.5 font-medium text-gray-500 text-xs uppercase tracking-wide">Status</th>
                <th className="px-5 py-3.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.map((item, i) => (
                <tr key={item.id} className="hover:bg-gray-50/40 transition-colors group">
                  <td className="px-5 py-4">
                    <span className="w-6 h-6 rounded-md bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center justify-center">
                      {i + 1}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <p className="font-medium text-gray-900 mb-1">{item.question}</p>
                    <p className="text-xs text-gray-400 line-clamp-1"
                      dangerouslySetInnerHTML={{ __html: item.answer?.replace(/<[^>]+>/g, ' ').trim() }}
                    />
                  </td>
                  <td className="px-5 py-4 text-gray-500 text-xs">{item.order}</td>
                  <td className="px-5 py-4">
                    <Badge color={item.isActive ? 'green' : 'gray'}>{item.isActive ? 'Active' : 'Inactive'}</Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                      <Link to={`/faq/${item.id}/edit`}>
                        <Button variant="ghost" size="xs" className="text-gray-400 hover:text-indigo-600">
                          <i className="ti ti-pencil" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="xs" className="text-gray-400 hover:text-red-600"
                        onClick={() => confirm({ title: 'Delete FAQ', message: 'Delete this question? This action cannot be undone.', onConfirm: () => deleteMut.mutate(item.id) })}>
                        <i className="ti ti-trash" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
